/**
 * `a1b2c3d · 12 Aug 14:02`, bottom-right, in the quietest ink the palette has.
 *
 * The one question it answers is "did the deploy arrive?", asked by the person who
 * deployed it, from a browser, without opening anything. So it is present on every screen
 * — a build is a property of the app, not of the session — and it is furniture rather than
 * interface: `aria-hidden`, no pointer events, and small enough that at reading distance
 * on a music desk it is a smudge in the corner.
 *
 * The time is formatted in the *viewer's* locale rather than a fixed one. It is being read
 * against the reader's own wall clock ("I pushed that five minutes ago"), which is exactly
 * the case `toLocaleString` exists for, and the sha is the half that has to be compared
 * character by character anyway.
 *
 * Mounted once, in `App`, outside the routes: the sketchbook pages do not go through
 * `AppFrame`, so hanging it off the frame would have quietly missed two screens.
 */

import { buildInfo } from '../../buildInfo.js';

/** Short and dateless-of-year: this is never read about a build from last November. */
const STAMP: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * The build time, or nothing.
 *
 * An empty or unparseable `builtAt` yields no second half rather than `Invalid Date`,
 * which is the sort of thing that makes a corner of the screen look broken.
 */
export function formatBuiltAt(builtAt: string): string {
  if (builtAt === '') return '';
  const at = new Date(builtAt);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, STAMP);
}

export function BuildBadge(): React.JSX.Element {
  const { sha, builtAt } = buildInfo();
  const stamp = formatBuiltAt(builtAt);
  return (
    <div className="hc-build" data-testid="hc-build-badge" aria-hidden="true">
      {stamp === '' ? sha : `${sha} · ${stamp}`}
    </div>
  );
}

export default BuildBadge;
